import type React from 'react';
import { useState, useCallback } from 'react';
import { useFieldContext } from '../FormContext';
import styles from './RangeField.module.css';

export const RangeField: React.FC = () => {
  const { field, value, onChange } = useFieldContext();
  const [inputValue, setInputValue] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  if (field.type !== 'range') {
    throw new Error('RangeField can only be used with range field type');
  }

  const currentValue = value ?? field.defaultValue ?? 0;

  const handleNumberInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const handleNumberInputBlur = useCallback(() => {
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(
        field.min ?? 0,
        Math.min(field.max ?? 100, numValue)
      );
      onChange(clampedValue);
    }
    setIsEditing(false);
    setInputValue('');
  }, [inputValue, onChange, field.min, field.max]);

  const handleNumberInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleNumberInputBlur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
        setInputValue('');
      }
    },
    [handleNumberInputBlur]
  );

  const handleValueClick = useCallback(() => {
    setIsEditing(true);
    setInputValue(currentValue.toString());
  }, [currentValue]);

  return (
    <div className={styles.container}>
      <label className={styles.label} htmlFor={field.name}>
        {field.label}
      </label>
      {field.description && (
        <p className={styles.description}>{field.description}</p>
      )}
      <div className={styles.inputContainer}>
        <input
          id={field.name}
          type="range"
          value={currentValue}
          min={field.min}
          max={field.max}
          step={field.step}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className={styles.rangeInput}
        />
        <div className={styles.valueContainer}>
          {isEditing ? (
            <input
              type="number"
              value={inputValue}
              onChange={handleNumberInputChange}
              onBlur={handleNumberInputBlur}
              onKeyDown={handleNumberInputKeyDown}
              min={field.min}
              max={field.max}
              step={field.step}
              className={styles.numberInput}
              autoFocus
            />
          ) : (
            <button
              type="button"
              onClick={handleValueClick}
              className={styles.valueButton}
              title="Click to edit value"
            >
              {currentValue}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
